import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'

type State = {
  /**
   * When we are visually pulling the page
   */
  isPulling: boolean
}

type Actions = {
  /**
   * Internal action not meant to set youself
   */
  setIsPulling: Dispatch<SetStateAction<boolean>>
}

type Context = State & Actions

const defaultValue = {}

export const PageRefreshContext = createContext<Context>(defaultValue as Context)

type PageRefreshProviderProps = PropsWithChildren

/**
 * Inner provider not meant for public!
 */
export function RefreshProvider({ children }: PageRefreshProviderProps) {
  const [isPulling, setIsPulling] = useState(false)

  const value = useMemo(
    (): Context => ({
      isPulling,
      setIsPulling,
    }),
    [isPulling, setIsPulling]
  )

  return <PageRefreshContext.Provider value={value}>{children}</PageRefreshContext.Provider>
}

/**
 * Internal logic where we decide when we are pulling
 */
export function usePageRefreshInternal() {
  const context = useContext(PageRefreshContext)

  if (context === defaultValue) {
    // reason: let our consumers know they actually are not using the hook inside a (sub)page with refresh
    // we do not throw it in thise case to not break their invalid implementation of an application with or without refresh
    // eslint-disable-next-line no-console
    console?.warn('not used within a RefreshProvider.')
  }

  return context
}

/**
 * You can now understand when we are actually pulling or not
 * Be sure your application is using the PageWithRefresh or SubPageWithRefresh
 * component arround your application.
 */
export function usePageRefresh(): Readonly<Pick<State, 'isPulling'>> {
  const { isPulling } = usePageRefreshInternal()
  return { isPulling }
}
